import React, { useState, useMemo } from "react";
import CustomTable from "components/Table/CustomTable";
import dayjs from "dayjs";
import StatusBadge from "components/Common/Badge/StatusBadge";
import { Link } from "react-router-dom";
import { useModal } from "contexts/ModalProvider";
import { SuspendedPartnersSchema } from "model/partners/suspended.schema";
import { Dropdown, DropdownMenu, DropdownToggle, DropdownItem } from "reactstrap";
import check from "assets/icons/check-square.svg";
import slash from "assets/icons/slash-circle.svg";
import useTableChangeHandler from "hooks/useTableChangeHandler";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import Avatar from "components/Avatar/Avatar";
import { useUpdatePartnerStatusMutation } from "services/partners/api";

const SuspendedPartnersTable = ({ data, error, isLoading, onTableChange }) => {
  const { handleSort, handleFilter, handleSearch, handlePageChange, forceRefresh } = useTableChangeHandler(onTableChange);

  const { modal } = useModal();
  const [selectedSuspendedPartners, setSelectedSuspendedPartners] = useState([]);
  const [actionsDropdown, setActionsDropdown] = useState({});
  const { addToast, removeToast } = useToasts();
  const [updatePartnerStatus] = useUpdatePartnerStatusMutation();

  const columns = useMemo(
    () => [
      {
        header: "Partners พาร์ทเนอร์",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="partner-info d-flex align-items-center gap-3">
            <Avatar src={item.profilePicture} alt={`${item.firstName} ${item.lastName}`} />
            <div>
              <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
              <div className="text-md text-gray-500">{item.email}</div>
            </div>
          </div>
        ),
      },
      {
        header: "ไอดีสมาชิก",
        accessor: "id",
        responsive: "lg",
      },
      {
        header: "วันที่สมัครสมาชิก",
        accessor: "registeredAt",
        align: "center",
        responsive: "md",
        render: (item) => dayjs(item.registeredAt).format("YYYY-MM-DD"),
      },
      {
        header: "เบอร์โทรศัพท์",
        accessor: "phoneNo",
        responsive: "md",
      },
      {
        header: "ข้อมูลโปรไฟล์",
        accessor: "info",
        align: "center",
        render: (item) => (
          <Link to={`/partners/${item.id}?source=banned`} className="light">
            <i className="mdi mdi-eye-outline" />
            ดูข้อมูล
          </Link>
        ),
      },
      {
        header: "สถานะ",
        accessor: "status",
        align: "center",
        render: (item) => <StatusBadge variant="danger">ถูกระงับการใช้งาน</StatusBadge>,
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <Dropdown
            size="thin"
            isOpen={actionsDropdown[item.id]}
            toggle={() =>
              setActionsDropdown({
                ...actionsDropdown,
                [item.id]: !actionsDropdown[item.id],
              })
            }
          >
            <DropdownToggle tag="span" className="cursor-pointer">
              <i className="mdi mdi-dots-vertical" />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => handleUnbanPartner(item)}>
                <img src={check} />
                ปลดแบนพาร์ทเนอร์
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        ),
      },
    ],
    [actionsDropdown, setActionsDropdown]
  );

  const renderUser = (item) => (
    <div className="partner-info d-flex align-items-center gap-3">
      <Avatar src={item.profilePicture} alt={`${item.firstName} ${item.lastName}`} />
      <div>
        <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
        <div className="text-md text-gray-500">{item.email}</div>
      </div>
    </div>
  );

  const handleUnbanPartner = async (item) => {
    const result = await modal.alert.primary({
      title: "คุณยืนยันที่จะปลดแบนพาร์ทเนอร์ท่านนี้หรือไม่?",
      message: `ตรวจสอบรายชื่อพาร์ทเนอร์ที่คุณต้องการปลดแบนว่าถูกต้องหรือไม่? เมื่อปลดแบนแล้ว รายชื่อพาร์ทเนอร์ที่ถูกปลดแบนจะไปอยู่ในแถบของ "พาร์ทเนอร์ในระบบ"`,
      confirmText: "ปลดแบนพาร์เนอร์",
      cancelText: "ยกเลิก",
      children: renderUser(item),
    });
    if (result) {
      try {
        // Update status to Approved (unban)
        await updatePartnerStatus({
          ids: [item.id],
          status: "Approved",
          source: "Suspended",
        });
        const toastId = addToast(<ToastNotification icon={check} title="ปลดแบนพาร์ทเนอร์แล้ว" description={`พาร์ทเนอร์ "${item.fname} ${item.lname}" ถูกปลดแบนแล้ว สามารถตรวจสอบได้ที่ "พาร์ทเนอร์ในระบบ"`} primaryButtonText="ดูพาร์ทเนอร์ในระบบ" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, {
          autoDismiss: true,
        });
        // Refresh data
        forceRefresh();
      } catch (error) {
        console.error("Failed to unban partner:", error);
        const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถปลดแบนพาร์ทเนอร์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} variant="danger" />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleUnbanAllPartner = async () => {
    if (selectedSuspendedPartners.length === 0) {
      const toastId = addToast(<ToastNotification icon={slash} title="ไม่มีรายการที่เลือก" description="กรุณาเลือกพาร์ทเนอร์ที่ต้องการปลดแบนอย่างน้อย 1 รายการ" onSecondaryClick={() => removeToast(toastId)} variant="warning" />, {
        autoDismiss: true,
      });
      return;
    }

    const result = await modal.alert.primary({
      title: "คุณยืนยันที่จะปลดแบนพาร์ทเนอร์ทั้งหมดนี้หรือไม่",
      message: `คุณได้ทำการกดปลดแบนรายการพาร์ทเนอร์ที่มีอยู่ในระบบทั้งหมด "${selectedSuspendedPartners.length} รายการ"`,
      confirmText: "ปลดแบนพาร์เนอร์",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {selectedSuspendedPartners.map((partner, index) => (
            <div key={partner.id} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              {renderUser(partner)}
            </div>
          ))}
        </div>
      ),
    });
    if (result) {
      try {
        await updatePartnerStatus({
          ids: selectedSuspendedPartners.map((partner) => partner.id),
          status: "Approved",
          source: "Suspended",
        });
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`ปลดแบนพาร์ทเนอร์แล้ว ${selectedSuspendedPartners.length} รายการ`} description="สามารถไปตรวจสอบพาร์ทเนอร์ที่ปลดแบนไปแล้วได้ที่ พาร์ทเนอร์ในระบบ" primaryButtonText="ดูพาร์ทเนอร์ในระบบ" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        forceRefresh();
      } catch (error) {
        console.error("Failed to unban partners:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถปลดแบนพาร์ทเนอร์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  return (
    <div className="table-container">
      <div className="text-lg mb-4">รายการพาร์ทเนอร์ Affiliate ที่ถูกแบนทั้งหมด *{data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        selectable={true}
        onSelectionChange={(ids) => setSelectedSuspendedPartners(ids)}
        selectionActions={
          <>
            <a className="success" onClick={handleUnbanAllPartner}>
              <i className="bx bx-check-circle" />
              <span className="d-none d-md-block">ยกเลิกการแบน</span>
            </a>
          </>
        }
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลพาร์ทเนอร์ที่ถูกแบน"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    // ตรวจสอบ validation เฉพาะเมื่อไม่มี error และไม่ได้กำลัง loading
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(SuspendedPartnersTable, SuspendedPartnersSchema);

export default ValidatedTable;
